import type { UiErrorShape } from '@orgus/protocol';

/** Every error shown in the UI: what failed, why, and what to do next. */
export type UiError = UiErrorShape & { status?: number };

/** Thrown by `api()`; carries a UiError ready to render. */
export class ApiError extends Error {
  readonly ui: UiError;
  readonly status: number;
  constructor(ui: UiError, status = 0) {
    super(ui.what);
    this.name = 'ApiError';
    this.ui = ui;
    this.status = status;
  }
}

export function isUiErrorShape(v: unknown): v is UiErrorShape {
  return !!v && typeof v === 'object' && typeof (v as UiErrorShape).what === 'string' && typeof (v as UiErrorShape).why === 'string';
}

/** Normalise anything thrown into { what, why, next }. Never returns raw "Something went wrong". */
export function toUiError(e: unknown, fallback: Partial<UiError> = {}): UiError {
  if (e instanceof ApiError) return e.ui;
  if (isUiErrorShape(e)) return { ...e, next: e.next || 'Try again in a moment.' };
  if (e instanceof DOMException && e.name === 'AbortError') {
    return { what: 'The request was cancelled.', why: 'It took too long or the page moved on.', next: 'Try again.' };
  }
  const message = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  return {
    what: fallback.what ?? 'That did not work.',
    why: fallback.why ?? (message ? `The browser reported: ${message}.` : 'The reason was not reported.'),
    next: fallback.next ?? 'Try again. If it keeps failing, check that the hub is running.',
  };
}

/**
 * The code of an error that is not a failure: the server does not run the feature (Orgus Cloud
 * before a module is ported). ErrorState shows it as a calm empty state, never in red.
 */
export const MODULE_ABSENT_CODE = 'module-absent';

/** Whether this thrown value or UiError says the feature is absent on this server. */
export function isModuleAbsent(e: unknown): boolean {
  const inner = e && typeof e === 'object' && 'ui' in e ? (e as { ui: unknown }).ui : e;
  const ui = isUiErrorShape(inner) ? inner : null;
  return ui?.code === MODULE_ABSENT_CODE;
}
